import dayjs from 'dayjs';

/**
 * Fetches customer invoice data for unpaid invoice reminder
 * @function
 * @param {string} supervisionId - The supervision ID
 * @param {function} dispatchAPI - API dispatch function
 * @returns {Promise<object>} Customer invoice data
 */
const fetchCustomerInvoice = async (supervisionId, dispatchAPI) => {
  const { data: subscriptionInvoiceData } = await dispatchAPI('GET', {
    url: `/customerinvoices?supervision=${supervisionId}&type=BALANCE`
  });

  if (subscriptionInvoiceData.length) {
    return subscriptionInvoiceData[0];
  }

  const { data: customerInvoiceData } = await dispatchAPI('GET', {
    url: `/customerinvoices/${supervisionId}`
  });

  return customerInvoiceData;
};

/**
 * Formats a date using dayjs
 * @function
 * @param {string|Date} date - The date to format
 * @param {string} format - The format string
 * @returns {string} Formatted date
 */
const formatDate = (date, format = 'dddd D MMMM YYYY') =>
  dayjs(date).format(format);

/**
 * Gets civility translation
 * @function
 * @param {string} civility - The civility value
 * @param {function} t - Translation function
 * @param {boolean} full - Whether to use full civility
 * @returns {string} Translated civility
 */
const getCivilityTranslation = (civility, t, full = false) => {
  if (!civility) return '';
  const prefix = full ? 'owners.tags.full_civility_' : 'owners.tags.';
  return t(`${prefix}${civility}`);
};

export const processedTemplateContent = async ({
  templateType,
  templateContent,
  collectionData,
  dispatchAPI,
  t
}) => {
  let anounce = null;
  let customerInvoice = null;
  if (templateType === 'UNPAID_INVOICE_REMINDER') {
    customerInvoice = await fetchCustomerInvoice(
      collectionData._id,
      dispatchAPI
    );
  }

  switch (templateType) {
    case 'UNPAID_INVOICE_REMINDER':
      return templateContent
        .replace('{{total_ttc}}', `${customerInvoice?.total_ttc} €`)
        .replace(
          '{{due_date}}',
          formatDate(customerInvoice?.due_date, 'DD/MM/YYYY')
        )
        .replace(
          '{{civility}}',
          getCivilityTranslation(collectionData?.customer?.civility, t, true)
        );
    case 'SEND_SUBSCRIPTION_REMINDER': {
      const { data } = await dispatchAPI('GET', {
        url: `/customerinvoices/${collectionData._id}`
      });

      customerInvoice = data;

      return templateContent.replace(
        '{{invoice_reference}}',
        `${customerInvoice?.reference} €`
      );
    }
    case 'SEND_INFORMATION_OWNER': {
      const mainInfo = collectionData?.main_informations;
      const animalText =
        mainInfo?.animals.length === 1 ? 'votre animal' : 'vos animaux';
      const startDate = mainInfo?.start_date;

      return templateContent
        .replace('{{animal_number}}', `${animalText} `)
        .replace('{{start_date}}', formatDate(startDate))
        .replace('{{end_date}}', formatDate(mainInfo?.end_date))
        .replace(
          '{{arrival_date}}',
          formatDate(dayjs(startDate).subtract(1, 'day'))
        );
    }
    case 'SEND_INFORMATION_PENSIONER': {
      const { data } = await dispatchAPI('GET', {
        url: `/anounces/form?supervision=${collectionData._id}&populate=supervision`
      });

      anounce = data[0];
      const mainInfo = collectionData?.main_informations;
      const owner = mainInfo?.owner;
      const startDate = mainInfo?.start_date;

      return templateContent
        .replace('{{supervision_reference}}', collectionData?.reference)
        .replace('{{start_date}}', formatDate(startDate))
        .replace('{{end_date}}', formatDate(mainInfo?.end_date))
        .replace(
          '{{arrival_date}}',
          formatDate(dayjs(startDate).subtract(1, 'day'))
        )
        .replace(
          '{{owner_civility}}',
          getCivilityTranslation(owner?.civility, t)
        )
        .replace('{{owner_last_name}}', owner?.last_name || '')
        .replace('{{owner_first_name}}', owner?.first_name || '')
        .replace('{{anounce_description}}', anounce?.description || '');
    }
    case 'CONTRACT_PENSIONER': {
      const owner = collectionData?.main_informations?.owner;
      return templateContent
        .replace('{{supervision_reference}}', collectionData?.reference)
        .replace(
          '{{owner_civility}}',
          getCivilityTranslation(owner?.civility, t)
        )
        .replace('{{owner_last_name}}', owner?.last_name || '')
        .replace('{{owner_first_name}}', owner?.first_name || '');
    }
    case 'CONTRACT_OWNER':
      return templateContent;
    case 'OWNER_SUPERVISION_VALIDATION': {
      let pensionersNames = '';

      if (collectionData?.pensioners?.length === 1) {
        const pensioner = collectionData.pensioners[0];
        pensionersNames = `${pensioner?.last_name || ''} ${
          pensioner?.first_name || ''
        }`;
      } else if (collectionData?.pensioners?.length > 1) {
        const firstPensioner = collectionData.pensioners[0];
        const secondPensioner = collectionData.pensioners[1];
        pensionersNames = `${firstPensioner?.last_name || ''} ${
          firstPensioner?.first_name || ''
        } et ${secondPensioner?.last_name || ''} ${
          secondPensioner?.first_name || ''
        }`;
      }
      return templateContent
        .replace('{{supervision_reference}}', collectionData?.reference)
        .replace('{{pensioner_name}}', pensionersNames || '');
    }
    case 'PENSIONER_SUPERVISION_VALIDATION': {
      const mainInfo = collectionData?.main_informations;
      return templateContent
        .replace('{{supervision_reference}}', collectionData?.reference)
        .replace('{{start_date}}', formatDate(mainInfo?.start_date))
        .replace('{{end_date}}', formatDate(mainInfo?.end_date));
    }

    case 'SEND_RESERVATION_REQUESTS_LIST': {
      const mainInfo = collectionData?.main_informations;
      return templateContent
        .replace('{{reference}}', collectionData?.reference)
        .replace('{{start_date}}', formatDate(mainInfo?.start_date))
        .replace('{{end_date}}', formatDate(mainInfo?.end_date));
    }

    default:
      return templateContent;
  }
};
